<template>
  <div class="flex w-full grow flex-col gap-4 overflow-y-auto p-4">
    <ui-event-modal-header :event="relatedEvent" @close="map.closePopup()" />

    <div class="flex w-full grow flex-col gap-y-2">
      <ui-tooltip-tile
        v-for="hazard in [
          ...relatedHazards.warnings,
          ...relatedHazards.centres,
          ...relatedHazards.outagesAndClosures,
          ...relatedHazards.incidents,
          ...relatedHazards.naturalHazards,
        ]"
        :key="hazard.id"
        :background-color="hazard.backgroundColor"
        :leading-color="hazard.leadingColor"
        :title="hazard.title"
        :description="hazard.description"
        :icon="hazard.icon"
        :issue-status="hazard.issueStatus"
        :selected="hazard.id === map.selectedPopupHazard?.hazardId"
        @icon:clicked="
          toggleSelectedHazard({
            hazardId: hazard.id,
            eventId: hazard.eventId!,
            hazardType: hazard.entityType,
          })
        "
      />
    </div>

    <ui-map-popup-footer
      left-text="Zoom map"
      :left-callback="zoomToFeatures"
      :right-callback="() => toggleSelectedHazard({ hazardId, eventId, hazardType })"
    />
  </div>
</template>

<script setup lang="ts">
import { EventEntity, HazardType, SelectedHazard, SelectedHazardViewType, useHazardsStore, useMapStore } from '@/features/hazards';
import { computed, watch } from 'vue';

const props = defineProps<{
  hazardType: HazardType;
  hazardId: string;
  eventId: string;
}>();

const map = useMapStore();

const hazardsStore = useHazardsStore();

// Get all the hazards related to the event.
const relatedHazards = computed(() => {
  return hazardsStore.getHazardsByEvent(props.eventId);
});

const hazard = computed(() => {
  return hazardsStore.hazards[props.hazardType].find((h) => h.id === props.hazardId);
});

const relatedEvent = computed<EventEntity | undefined>(() => {
  return hazardsStore.hazards.events.find((event) => event.id === props.eventId);
});

const pointFeatures = computed(() => {
  const incident = relatedHazards.value?.incidents.find((incident) => incident.id === map.selectedPopupHazard?.hazardId);
  const warning = relatedHazards.value?.warnings.find((warning) => warning.id === map.selectedPopupHazard?.hazardId);
  const outageOrClosure = relatedHazards.value?.outagesAndClosures.find(
    (outageOrClosure) => outageOrClosure.id === map.selectedPopupHazard?.hazardId
  );
  const centre = relatedHazards.value?.centres.find((centre) => centre.id === map.selectedPopupHazard?.hazardId);

  return (warning ?? incident ?? outageOrClosure ?? centre)?.pointFeatures;
});

watch(hazard, (incomingHazard, previousHazard) => {
  if (incomingHazard || !previousHazard) return;

  map.closePopup();
  hazardsStore.toggleSelectHazard(null);
});

const toggleSelectedHazard = (hazard: SelectedHazard) => {
  map.closePopup();
  hazardsStore.toggleSelectHazard({ ...hazard, viewType: SelectedHazardViewType.Modal });
};

const zoomToFeatures = () => {
  map.flyToFeatures(pointFeatures.value);
};
</script>
